import * as React from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { LineChart, ChartsReferenceLine } from '@mui/x-charts';
import { useTheme } from '@mui/material/styles';

// Import live sensor context
import { useLiveSensors as useLiveSensorsHook } from '../context/LiveSensorContext';

// All available sensors with their configurations - more descriptive labels
const ALL_SENSORS = {
  ph: { id: 'ph', label: 'pH Level', shortLabel: 'pH', unit: '', min: 0, max: 14, optimalMin: 5.5, optimalMax: 6.5, color: '#8b5cf6', category: 'Water Reservoir' },
  ec: { id: 'ec', label: 'Electrical Conductivity', shortLabel: 'EC', unit: 'mS/cm', min: 0, max: 5, optimalMin: 1.2, optimalMax: 2.0, color: '#06b6d4', category: 'Water Reservoir' },
  tds: { id: 'tds', label: 'Total Dissolved Solids', shortLabel: 'TDS', unit: 'ppm', min: 0, max: 2000, optimalMin: 600, optimalMax: 1000, color: '#eab308', category: 'Water Reservoir' },
  waterTemp: { id: 'waterTemp', label: 'Water Temperature', shortLabel: 'Water Temp', unit: '°F', min: 50, max: 90, optimalMin: 65, optimalMax: 72, color: '#14b8a6', category: 'Water Reservoir' },
  orp: { id: 'orp', label: 'Oxidation Reduction', shortLabel: 'ORP', unit: 'mV', min: 0, max: 700, optimalMin: 300, optimalMax: 500, color: '#a855f7', category: 'Water Reservoir' },
  airTemp: { id: 'airTemp', label: 'Air Temperature', shortLabel: 'Air Temp', unit: '°F', min: 60, max: 95, optimalMin: 75, optimalMax: 85, color: '#ef4444', category: 'Atmosphere' },
  humidity: { id: 'humidity', label: 'Relative Humidity', shortLabel: 'Humidity', unit: '%', min: 20, max: 90, optimalMin: 50, optimalMax: 70, color: '#3b82f6', category: 'Atmosphere' },
  co2: { id: 'co2', label: 'Carbon Dioxide', shortLabel: 'CO₂', unit: 'ppm', min: 400, max: 2000, optimalMin: 1000, optimalMax: 1500, color: '#22c55e', category: 'Atmosphere' },
  vpd: { id: 'vpd', label: 'Vapor Pressure Deficit', shortLabel: 'VPD', unit: 'kPa', min: 0, max: 2, optimalMin: 0.8, optimalMax: 1.2, color: '#f97316', category: 'Atmosphere' },
  ppfd: { id: 'ppfd', label: 'Photon Flux Density', shortLabel: 'PPFD', unit: 'µmol/m²/s', min: 0, max: 1500, optimalMin: 600, optimalMax: 1000, color: '#fbbf24', category: 'Lighting' },
  dli: { id: 'dli', label: 'Daily Light Integral', shortLabel: 'DLI', unit: 'mol/m²/d', min: 0, max: 65, optimalMin: 30, optimalMax: 50, color: '#f59e0b', category: 'Lighting' },
};

// Default mock values for non-demo mode
const DEFAULT_MOCK_VALUES = {
  ph: 6.2,
  ec: 1.8,
  tds: 890,
  waterTemp: 68.5,
  orp: 320,
  airTemp: 78.2,
  humidity: 62,
  co2: 1150,
  vpd: 1.05,
  ppfd: 850,
  dli: 38.5,
};

// Generate mock time series data
const generateMockTimeSeriesData = (sensorId, currentValue, points = 24) => {
  const sensor = ALL_SENSORS[sensorId];
  const data = [];
  const now = new Date();
  const range = sensor.max - sensor.min;

  for (let i = points - 1; i >= 0; i--) {
    const time = new Date(now.getTime() - i * 60 * 60 * 1000);
    const variation = (Math.random() - 0.5) * range * 0.15;
    const value = Math.max(sensor.min, Math.min(sensor.max, currentValue + variation));
    data.push({ time, value: Number(value.toFixed(2)) });
  }
  data[data.length - 1].value = currentValue;
  return data;
};

export default function SensorOverview({ onRemove, defaultSensors = [], growId }) {
  const theme = useTheme();
  const location = useLocation();
  const [addMenuAnchor, setAddMenuAnchor] = React.useState(null);

  // Detect demo mode
  const isDemo = location.pathname.startsWith('/demo');

  // Always call the hook unconditionally (returns null if not in provider)
  const liveSensorContext = useLiveSensorsHook();
  // Only use live data in demo mode
  const liveSensorData = isDemo ? liveSensorContext : null;

  // Get current sensor values - use live data in demo mode
  const getSensorValue = React.useCallback((sensorId) => {
    if (liveSensorData && liveSensorData.sensorValues[sensorId] !== undefined) {
      return liveSensorData.sensorValues[sensorId];
    }
    return DEFAULT_MOCK_VALUES[sensorId] || 0;
  }, [liveSensorData]);

  // Get sensor history - use live data in demo mode
  const getSensorHistory = React.useCallback((sensorId) => {
    if (liveSensorData && liveSensorData.sensorHistory[sensorId]) {
      return liveSensorData.sensorHistory[sensorId].map(h => ({
        time: new Date(h.time),
        value: h.value,
      }));
    }
    return generateMockTimeSeriesData(sensorId, getSensorValue(sensorId), 24);
  }, [liveSensorData, getSensorValue]);

  // Initialize with defaultSensors if provided, otherwise use fallback
  const [selectedSensors, setSelectedSensors] = React.useState(() => {
    if (defaultSensors && defaultSensors.length > 0) {
      // Use sensors from the grow's modules, limited to what we have in ALL_SENSORS
      return defaultSensors.filter(s => ALL_SENSORS[s]).slice(0, 10);
    }
    return ['ph', 'airTemp', 'humidity'];
  });

  // Update selected sensors when defaultSensors changes (e.g., when sensors are added to modules)
  React.useEffect(() => {
    if (defaultSensors && defaultSensors.length > 0) {
      setSelectedSensors(prev => {
        // Merge: keep existing selections and add new ones from modules
        const newSensors = new Set([...prev]);
        defaultSensors.forEach(s => {
          if (ALL_SENSORS[s]) {
            newSensors.add(s);
          }
        });
        return Array.from(newSensors).slice(0, 10);
      });
    }
  }, [defaultSensors]);

  const handleAddClick = (event) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddClose = () => {
    setAddMenuAnchor(null);
  };

  const toggleSensor = (sensorId) => {
    if (selectedSensors.includes(sensorId)) {
      setSelectedSensors(prev => prev.filter(id => id !== sensorId));
    } else if (selectedSensors.length < 10) {
      setSelectedSensors(prev => [...prev, sensorId]);
    }
  };

  const removeSensor = (sensorId) => {
    setSelectedSensors(prev => prev.filter(id => id !== sensorId));
  };

  // Generate data for selected sensors - use live data in demo mode
  const chartData = React.useMemo(() => {
    if (selectedSensors.length === 0) return [];

    // Get history for first sensor to determine time points
    const firstHistory = getSensorHistory(selectedSensors[0]);
    const data = firstHistory.map((h) => ({ time: h.time }));

    selectedSensors.forEach(sensorId => {
      const sensorHistory = getSensorHistory(sensorId);
      sensorHistory.forEach((d, i) => {
        if (i < data.length) {
          const sensor = ALL_SENSORS[sensorId];
          const midpoint = (sensor.min + sensor.max) / 2;
          const halfRange = (sensor.max - sensor.min) / 2;
          const percentOff = ((d.value - midpoint) / halfRange) * 100;
          data[i][sensorId] = d.value;
          data[i][`${sensorId}_normalized`] = percentOff;
        }
      });
    });

    return data;
  }, [selectedSensors, getSensorHistory]);

  const xAxisData = chartData.map(d => d.time);

  // Calculate fixed time window for smooth streaming (always show full width)
  const timeWindow = React.useMemo(() => {
    if (xAxisData.length === 0) return { min: new Date(), max: new Date() };
    // Always use a fixed 2.5 minute window from the latest data point
    const maxTime = xAxisData[xAxisData.length - 1] || new Date();
    const minTime = new Date(maxTime.getTime() - 150000); // 150 seconds = 2.5 minutes
    return { min: minTime, max: maxTime };
  }, [xAxisData]);

  // Create series for each selected sensor
  const series = selectedSensors.map(sensorId => {
    const sensor = ALL_SENSORS[sensorId];
    return {
      data: chartData.map(d => d[`${sensorId}_normalized`]),
      label: sensor.shortLabel,
      color: sensor.color,
      showMark: false,
      curve: 'monotoneX',
      valueFormatter: (v, { dataIndex }) => {
        const actual = chartData[dataIndex];
        return actual ? `${actual[sensorId]} ${sensor.unit}` : '';
      },
    };
  });

  // Group sensors by category for the menu
  const sensorsByCategory = Object.values(ALL_SENSORS).reduce((acc, sensor) => {
    if (!acc[sensor.category]) acc[sensor.category] = [];
    acc[sensor.category].push(sensor);
    return acc;
  }, {});

  return (
    <Card variant="outlined" sx={{ height: { xs: 'auto', md: '100%' } }}>
      <CardContent sx={{ p: { xs: 1, md: 2 }, '&:last-child': { pb: { xs: 1, md: 2 } } }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: { xs: '0.9rem', md: '1.25rem' } }}
            >
              Sensor Overview
            </Typography>
            {isDemo && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: { xs: 0.75, md: 1 },
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'success.main',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 5, md: 6 },
                    height: { xs: 5, md: 6 },
                    borderRadius: '50%',
                    bgcolor: 'white',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'white', fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: { xs: '0.55rem', md: '0.65rem' } }}
                >
                  LIVE
                </Typography>
              </Box>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="Add sensor">
              <IconButton
                size="small"
                onClick={handleAddClick}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: { xs: 20, md: 24 },
                  height: { xs: 20, md: 24 },
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <AddIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
              </IconButton>
            </Tooltip>
            {onRemove && (
              <IconButton size="small" onClick={onRemove}>
                <CloseIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Main content - responsive layout */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1, md: 2 }}
        >
          {/* Chart area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selectedSensors.length > 0 ? (
              <LineChart
                height={200}
                xAxis={[{
                  data: xAxisData,
                  scaleType: 'time',
                  min: timeWindow.min,
                  max: timeWindow.max,
                  tickNumber: 4,
                  tickLabelStyle: {
                    fontSize: 7,
                    fill: theme.palette.text.secondary,
                    fontFamily: 'Inter, sans-serif',
                  },
                }]}
                yAxis={[{
                  min: -50,
                  max: 50,
                  tickNumber: 5,
                  tickLabelStyle: {
                    fontSize: 7,
                    fill: theme.palette.text.secondary,
                    fontFamily: 'Inter, sans-serif',
                  },
                  valueFormatter: (v) => `${v > 0 ? '+' : ''}${v}%`,
                }]}
                series={series}
                margin={{ top: 5, right: 5, bottom: 20, left: 30 }}
                skipAnimation
                slotProps={{
                  legend: { hidden: true },
                }}
                sx={{
                  '& .MuiLineElement-root': {
                    strokeWidth: 2,
                    transition: 'none',
                  },
                  '& .MuiChartsAxis-root': {
                    transition: 'none',
                  },
                }}
              >
                <ChartsReferenceLine
                  y={0}
                  lineStyle={{
                    stroke: theme.palette.text.secondary,
                    strokeWidth: 1.5,
                  }}
                />
                <ChartsReferenceLine
                  y={15}
                  lineStyle={{
                    stroke: theme.palette.warning.main,
                    strokeWidth: 1,
                    strokeDasharray: '5 3',
                  }}
                />
                <ChartsReferenceLine
                  y={-15}
                  lineStyle={{
                    stroke: theme.palette.warning.main,
                    strokeWidth: 1,
                    strokeDasharray: '5 3',
                  }}
                />
              </LineChart>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  Add sensors to display
                </Typography>
              </Box>
            )}
          </Box>

          {/* Legend - below on mobile, right side on desktop */}
          <Box
            sx={{
              width: { xs: '100%', md: 200 },
              flexShrink: 0,
              borderTop: { xs: 1, md: 0 },
              borderLeft: { xs: 0, md: 1 },
              borderColor: 'divider',
              pt: { xs: 1, md: 0 },
              pl: { xs: 0, md: 2 },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'block',
                mb: 0.5,
                fontSize: { xs: '0.55rem', md: '0.65rem' },
              }}
            >
              Current Readings ({selectedSensors.length}/10)
            </Typography>

            {/* Mobile: Grid layout like LandingSensorOverview */}
            <Box
              sx={{
                display: { xs: 'grid', md: 'none' },
                gridTemplateColumns: selectedSensors.length <= 3 ? `repeat(${selectedSensors.length}, 1fr)` : 'repeat(auto-fit, minmax(60px, 1fr))',
                gap: 0.5,
              }}
            >
              {selectedSensors.map(sensorId => {
                const sensor = ALL_SENSORS[sensorId];
                const currentValue = getSensorValue(sensorId);
                const isOptimal = currentValue >= sensor.optimalMin && currentValue <= sensor.optimalMax;

                return (
                  <Box
                    key={sensorId}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                      p: 0.5,
                      borderRadius: 1,
                      bgcolor: `${sensor.color}15`,
                      border: `1px solid ${sensor.color}40`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: sensor.color,
                        boxShadow: `0 0 4px ${sensor.color}`,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.55rem',
                        lineHeight: 1.1,
                        textAlign: 'center',
                      }}
                    >
                      {sensor.shortLabel}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        color: isOptimal ? 'success.main' : 'warning.main',
                        fontSize: '0.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {typeof currentValue === 'number' ? currentValue.toFixed(sensor.unit === 'ppm' ? 0 : 1) : currentValue} {sensor.unit}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Desktop: Stack layout with remove buttons */}
            <Stack spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' }, maxHeight: 280, overflow: 'auto' }}>
              {selectedSensors.map(sensorId => {
                const sensor = ALL_SENSORS[sensorId];
                const currentValue = getSensorValue(sensorId);
                const isOptimal = currentValue >= sensor.optimalMin && currentValue <= sensor.optimalMax;
                const valueColor = isOptimal ? 'success.main' :
                  (currentValue < sensor.optimalMin * 0.8 || currentValue > sensor.optimalMax * 1.2) ? 'error.main' : 'warning.main';

                return (
                  <Box
                    key={sensorId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: `${sensor.color}10`,
                      border: `1px solid ${sensor.color}30`,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: sensor.color,
                        flexShrink: 0,
                        boxShadow: isDemo ? `0 0 6px ${sensor.color}` : 'none',
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          display: 'block',
                          lineHeight: 1.2,
                        }}
                      >
                        {sensor.shortLabel}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          color: valueColor,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {typeof currentValue === 'number' ? currentValue.toFixed(sensor.unit === 'ppm' ? 0 : 1) : currentValue} {sensor.unit}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeSensor(sensorId)}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                );
              })}

              {selectedSensors.length === 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'Inter, sans-serif',
                    textAlign: 'center',
                    py: 2,
                  }}
                >
                  No sensors selected
                </Typography>
              )}
            </Stack>

            {/* Mobile: Empty state */}
            {selectedSensors.length === 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: { xs: 'block', md: 'none' },
                  color: 'text.secondary',
                  fontFamily: 'Inter, sans-serif',
                  textAlign: 'center',
                  py: 1,
                  fontSize: '0.65rem',
                }}
              >
                No sensors selected
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Add sensor menu */}
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={handleAddClose}
          PaperProps={{
            sx: { maxHeight: 400, width: 280 },
          }}
        >
          {Object.entries(sensorsByCategory).map(([category, sensors]) => (
            <Box key={category}>
              <MenuItem disabled sx={{ opacity: 0.7, py: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    letterSpacing: 0.5,
                  }}
                >
                  {category}
                </Typography>
              </MenuItem>
              {sensors.map(sensor => (
                <MenuItem
                  key={sensor.id}
                  onClick={() => toggleSensor(sensor.id)}
                  disabled={selectedSensors.length >= 10 && !selectedSensors.includes(sensor.id)}
                  sx={{ py: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      checked={selectedSensors.includes(sensor.id)}
                      size="small"
                      sx={{
                        color: sensor.color,
                        '&.Mui-checked': { color: sensor.color },
                      }}
                    />
                  </ListItemIcon>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif' }}>
                      {sensor.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                    >
                      {sensor.shortLabel} • {sensor.unit || 'no unit'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: sensor.color,
                      ml: 1,
                    }}
                  />
                </MenuItem>
              ))}
            </Box>
          ))}
        </Menu>
      </CardContent>
    </Card>
  );
}
